// MIDI Visualizer Utilities
// Handles coloring, dimension setting, and instrument analysis for html-midi-player

// Seaborn tab20 palette colors
const SEABORN_TAB20_COLORS = [ // from `sns.color_palette('tab20', desat=0.6)`
    '#3d7296', '#bac9dc', '#cf823e', '#e4bb93',
    '#438943', '#a3ce9b', '#b34a4b', '#eaacab', 
    '#9378ac', '#c4b7ce', '#7f5f58', '#baa29e', 
    '#cd8dba', '#eac3d4', '#7f7f7f', '#c7c7c7',
    '#9d9e41', '#cbcb9d', '#3ca0aa', '#acd0d7'
];

// const SEABORN_TAB20_COLORS_ACTIVE = [ // from sns.color_palette('tab20')
//     '#1f77b4', '#aec7e8', '#ff7f0e', '#ffbb78',
//     '#2ca02c', '#98df8a', '#d62728', '#ff9896',
//     '#9467bd', '#c5b0d5', '#8c564b', '#c49c94',
//     '#e377c2', '#f7b6d2', '#7f7f7f', '#c7c7c7',
//     '#bcbd22', '#dbdb8d', '#17becf', '#9edae5'
// ]

const restOpacity = 0.3
const activeOpacity = 0.85

// Create color sequence as specified: [0], [2], [4], ..., [18], [1], [3], ..., [19]
const createColorSequence = () => {
  const sequence = [];
  // Add even indices first: 0, 2, 4, ..., 18
  for (let i = 0; i < 20; i += 2) {
    sequence.push(i);
  }
  // Add odd indices: 1, 3, 5, ..., 19
  for (let i = 1; i < 20; i += 2) {
    sequence.push(i);
  }
  return sequence;
};

const COLOR_SEQUENCE = createColorSequence();

// MIDI Instrument ID to Name and Abbreviation mapping (General MIDI Standard)
const MIDI_INSTRUMENTS = {
  // Piano Family (0-7)
  0: { abbr: "Pn", name: "Acoustic Grand Piano" },
  1: { abbr: "Pn", name: "Bright Acoustic Piano" },
  2: { abbr: "Pn", name: "Electric Grand Piano" },
  3: { abbr: "Pn", name: "Honky-tonk Piano" },
  4: { abbr: "EP", name: "Electric Piano 1" },
  5: { abbr: "EP", name: "Electric Piano 2" },
  6: { abbr: "Hp", name: "Harpsichord" },
  7: { abbr: "Cv", name: "Clavinet" },
  
  // Chromatic Percussion (8-15)
  8: { abbr: "Ce", name: "Celesta" },
  9: { abbr: "Gl", name: "Glockenspiel" },
  10: { abbr: "Mb", name: "Music Box" },
  11: { abbr: "Vi", name: "Vibraphone" },
  12: { abbr: "Xy", name: "Marimba" },
  13: { abbr: "Xy", name: "Xylophone" },
  14: { abbr: "Tb", name: "Tubular Bells" },
  15: { abbr: "Du", name: "Dulcimer" },
  
  // Organ (16-23)
  16: { abbr: "Or", name: "Drawbar Organ" },
  17: { abbr: "Or", name: "Percussive Organ" },
  18: { abbr: "Or", name: "Rock Organ" },
  19: { abbr: "Or", name: "Church Organ" },
  20: { abbr: "Or", name: "Reed Organ" },
  21: { abbr: "Ac", name: "Accordion" },
  22: { abbr: "Ha", name: "Harmonica" },
  23: { abbr: "Ac", name: "Tango Accordion" },
  
  // Guitar (24-31)
  24: { abbr: "AG", name: "Acoustic Guitar (nylon)" },
  25: { abbr: "AG", name: "Acoustic Guitar (steel)" },
  26: { abbr: "EG", name: "Electric Guitar (jazz)" },
  27: { abbr: "EG", name: "Electric Guitar (clean)" },
  28: { abbr: "EG", name: "Electric Guitar (muted)" },
  29: { abbr: "EG", name: "Overdriven Guitar" },
  30: { abbr: "EG", name: "Distortion Guitar" },
  31: { abbr: "EG", name: "Guitar Harmonics" },
  
  // Bass (32-39)
  32: { abbr: "Ba", name: "Acoustic Bass" },
  33: { abbr: "Ba", name: "Electric Bass (finger)" },
  34: { abbr: "Ba", name: "Electric Bass (pick)" },
  35: { abbr: "Ba", name: "Fretless Bass" },
  36: { abbr: "Ba", name: "Slap Bass 1" },
  37: { abbr: "Ba", name: "Slap Bass 2" },
  38: { abbr: "SB", name: "Synth Bass 1" },
  39: { abbr: "SB", name: "Synth Bass 2" },
  
  // Strings (40-47)
  40: { abbr: "Vn", name: "Violin" },
  41: { abbr: "Va", name: "Viola" },
  42: { abbr: "Vc", name: "Cello" },
  43: { abbr: "Db", name: "Contrabass" },
  44: { abbr: "Tr", name: "Tremolo Strings" },
  45: { abbr: "Pz", name: "Pizzicato Strings" },
  46: { abbr: "Hp", name: "Orchestral Harp" },
  47: { abbr: "Tp", name: "Timpani" },
  
  // Ensemble (48-55)
  48: { abbr: "St", name: "String Ensemble 1" },
  49: { abbr: "St", name: "String Ensemble 2" },
  50: { abbr: "St", name: "Synth Strings 1" },
  51: { abbr: "St", name: "Synth Strings 2" },
  52: { abbr: "Ah", name: "Choir Aahs" },
  53: { abbr: "Oh", name: "Voice Oohs" },
  54: { abbr: "Ch", name: "Synth Choir" },
  55: { abbr: "Or", name: "Orchestra Hit" },
  
  // Brass (56-63)
  56: { abbr: "Tp", name: "Trumpet" },
  57: { abbr: "Tb", name: "Trombone" },
  58: { abbr: "Tu", name: "Tuba" },
  59: { abbr: "Tp", name: "Muted Trumpet" },
  60: { abbr: "Hr", name: "French Horn" },
  61: { abbr: "Br", name: "Brass Section" },
  62: { abbr: "SB", name: "Synth Brass 1" },
  63: { abbr: "SB", name: "Synth Brass 2" },
  
  // Reed (64-71)
  64: { abbr: "Sx", name: "Soprano Sax" },
  65: { abbr: "Sx", name: "Alto Sax" },
  66: { abbr: "Sx", name: "Tenor Sax" },
  67: { abbr: "Sx", name: "Baritone Sax" },
  68: { abbr: "Ob", name: "Oboe" },
  69: { abbr: "Eh", name: "English Horn" },
  70: { abbr: "Fg", name: "Bassoon" },
  71: { abbr: "Cl", name: "Clarinet" },
  
  // Pipe (72-79)
  72: { abbr: "Pc", name: "Piccolo" },
  73: { abbr: "Fl", name: "Flute" },
  74: { abbr: "Rc", name: "Recorder" },
  75: { abbr: "Pf", name: "Pan Flute" },
  76: { abbr: "Bt", name: "Blown Bottle" },
  77: { abbr: "Sh", name: "Shakuhachi" },
  78: { abbr: "Wh", name: "Whistle" },
  79: { abbr: "Oc", name: "Ocarina" },
  
  // Synth Lead (80-87)
  80: { abbr: "Sn", name: "Synth Lead 1 (square)" },
  81: { abbr: "Sn", name: "Synth Lead 2 (sawtooth)" },
  82: { abbr: "Sn", name: "Synth Lead 3 (calliope)" },
  83: { abbr: "Sn", name: "Synth Lead 4 (chiff)" },
  84: { abbr: "Sn", name: "Synth Lead 5 (charang)" },
  85: { abbr: "Sn", name: "Synth Lead 6 (voice)" },
  86: { abbr: "Sn", name: "Synth Lead 7 (fifths)" },
  87: { abbr: "Sn", name: "Synth Lead 8 (bass + lead)" },
  
  // Synth Pad (88-95)
  88: { abbr: "Sn", name: "Synth Pad 1 (new age)" },
  89: { abbr: "Sn", name: "Synth Pad 2 (warm)" },
  90: { abbr: "Sn", name: "Synth Pad 3 (polysynth)" },
  91: { abbr: "Sn", name: "Synth Pad 4 (choir)" },
  92: { abbr: "Sn", name: "Synth Pad 5 (bowed)" },
  93: { abbr: "Sn", name: "Synth Pad 6 (metallic)" },
  94: { abbr: "Sn", name: "Synth Pad 7 (halo)" },
  95: { abbr: "Sn", name: "Synth Pad 8 (sweep)" },
  
  // Synth Effects (96-103)
  96: { abbr: "Fx", name: "FX 1 (rain)" },
  97: { abbr: "Fx", name: "FX 2 (soundtrack)" },
  98: { abbr: "Fx", name: "FX 3 (crystal)" },
  99: { abbr: "Fx", name: "FX 4 (atmosphere)" },
  100: { abbr: "Fx", name: "FX 5 (brightness)" },
  101: { abbr: "Fx", name: "FX 6 (goblins)" },
  102: { abbr: "Fx", name: "FX 7 (echoes)" },
  103: { abbr: "Fx", name: "FX 8 (sci-fi)" },
  
  // Ethnic (104-111)
  104: { abbr: "Si", name: "Sitar" },
  105: { abbr: "Bj", name: "Banjo" },
  106: { abbr: "Sh", name: "Shamisen" },
  107: { abbr: "Kt", name: "Koto" },
  108: { abbr: "Kl", name: "Kalimba" },
  109: { abbr: "Bp", name: "Bagpipe" },
  110: { abbr: "Fd", name: "Fiddle" },
  111: { abbr: "Sn", name: "Shanai" },
  
  // Percussive (112-119)
  112: { abbr: "Tb", name: "Tinkle Bell" },
  113: { abbr: "Ag", name: "Agogo" },
  114: { abbr: "SD", name: "Steel Drums" },
  115: { abbr: "Wb", name: "Woodblock" },
  116: { abbr: "Tk", name: "Taiko Drum" },
  117: { abbr: "MT", name: "Melodic Tom" },
  118: { abbr: "SD", name: "Synth Drum" },
  119: { abbr: "Cy", name: "Reverse Cymbal" },
  
  // Sound Effects (120-127)
  120: { abbr: "Fx", name: "Guitar Fret Noise" },
  121: { abbr: "Fx", name: "Breath Noise" },
  122: { abbr: "Fx", name: "Seashore" },
  123: { abbr: "Fx", name: "Bird Tweet" },
  124: { abbr: "Fx", name: "Telephone Ring" },
  125: { abbr: "Fx", name: "Helicopter" },
  126: { abbr: "Fx", name: "Applause" },
  127: { abbr: "Fx", name: "Gunshot" }
};

// Function to get instrument info by ID
const getInstrumentInfo = (instrumentId) => {
  return MIDI_INSTRUMENTS[instrumentId] || { abbr: instrumentId.toString(), name: `Instrument ${instrumentId}` };
};

// Function to convert hex to rgba with alpha
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Function to analyze MIDI data and get instrument note counts
export const analyzeMidiInstruments = async (dataUrl) => {
  try {
    // Use Magenta.js to parse the MIDI data - check different possible global names
    const magenta = window.core || window.mm || window.magenta;
    if (!magenta || !magenta.urlToNoteSequence) {
      throw new Error('Magenta.js core module not available');
    }
    const noteSequence = await magenta.urlToNoteSequence(dataUrl);
    
    // Count notes per instrument (program number), excluding drums
    const instrumentCounts = {};
    let drumNoteCount = 0;
    
    if (noteSequence.notes) {
      noteSequence.notes.forEach(note => {
        // Check if this is a drum note (usually channel 9/10 or isDrum property)
        const isDrum = note.isDrum || 
                      (note.channel !== undefined && note.channel === 9) ||
                      (note.program !== undefined && note.program === 128); // GM drum kit
        
        if (isDrum) {
          drumNoteCount++;
        } else {
          // Use program number (actual MIDI instrument ID) instead of instrument field
          const programNumber = note.program !== undefined ? note.program : (note.instrument || 0);
          instrumentCounts[programNumber] = (instrumentCounts[programNumber] || 0) + 1;
        }
      });
    }
    
    // console.log('MIDI Analysis Results:');
    // console.log('- Drum notes found:', drumNoteCount);
    // console.log('- Program number counts:', instrumentCounts);
    
    // Log instrument names for debugging
    Object.keys(instrumentCounts).forEach(programNumber => {
      const info = getInstrumentInfo(parseInt(programNumber));
      console.log(`  Program ${programNumber}: ${info.name} (${info.abbr}) - ${instrumentCounts[programNumber]} notes`);
    });
    
    // Sort instruments by note count (descending)
    const sortedInstruments = Object.entries(instrumentCounts)
      .sort(([, a], [, b]) => b - a)
      .map(([instrument]) => parseInt(instrument));
    
    return {
      instruments: sortedInstruments,
      hasDrums: drumNoteCount > 0
    };
  } catch (error) {
    console.warn('Failed to analyze MIDI instruments:', error);
    // Fallback: assume instruments 0-15 in order
    return {
      instruments: Array.from({length: 16}, (_, i) => i),
      hasDrums: false
    };
  }
};

// Function to generate and inject CSS for instrument colors
export const generateInstrumentColorCSS = (visualizerId, instrumentOrder) => {
  // Create CSS rules for each instrument
  let cssRules = '';
  
  instrumentOrder.forEach((instrument, index) => {
    if (index < COLOR_SEQUENCE.length) {
      const colorIndex = COLOR_SEQUENCE[index];
      const restColor = hexToRgba(SEABORN_TAB20_COLORS[colorIndex], restOpacity);
      const activeColor = hexToRgba(SEABORN_TAB20_COLORS[colorIndex], activeOpacity);
      
      // CSS rules for this instrument - try multiple attribute selectors
      cssRules += `
        #${visualizerId} svg rect.note[data-program="${instrument}"],
        #${visualizerId} svg rect.note[data-instrument="${instrument}"] {
          fill: ${restColor} !important;
        }
        
        #${visualizerId} svg rect.note[data-program="${instrument}"].active,
        #${visualizerId} svg rect.note[data-instrument="${instrument}"].active {
          fill: ${activeColor} !important;
          filter: brightness(1.2) saturate(1.2) !important;
        }
      `;
    }
  });
  
  // Special handling for drums - target multiple possible selectors with updated colors
  const drumRestColor = hexToRgba('#000000', restOpacity);
  const drumActiveColor = '#000000';
  
  cssRules += `
    #${visualizerId} svg rect.note[data-is-drum="true"],
    #${visualizerId} svg rect.note[data-channel="9"],
    #${visualizerId} svg rect.note[data-program="128"],
    #${visualizerId} svg rect.note[data-instrument="128"] {
      fill: ${drumRestColor} !important;
      opacity: 0.8 !important;
    }
    
    #${visualizerId} svg rect.note[data-is-drum="true"].active,
    #${visualizerId} svg rect.note[data-channel="9"].active,
    #${visualizerId} svg rect.note[data-program="128"].active,
    #${visualizerId} svg rect.note[data-instrument="128"].active {
      fill: ${drumActiveColor} !important;
      opacity: ${activeOpacity} !important;
      filter: brightness(1) saturate(1) !important;
    }
  `;
  
  // Create or update style element
  let styleElement = document.getElementById(`midi-colors-${visualizerId}`);
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = `midi-colors-${visualizerId}`;
    document.head.appendChild(styleElement);
  }
  
  styleElement.textContent = cssRules;
  
  console.log('Generated CSS for instruments:', instrumentOrder);
  
  // Debug: Log SVG note attributes after a short delay
  setTimeout(() => {
    const visualizer = document.getElementById(visualizerId);
    if (visualizer) {
      const notes = visualizer.querySelectorAll('svg rect.note');
      if (notes.length > 0) {
        console.log(`Found ${notes.length} notes in visualizer. Sample note attributes:`, Array.from(notes[0].attributes).map(attr => `${attr.name}="${attr.value}"`));
        console.log('First few notes program/instrument values:', Array.from(notes).slice(0, 10).map(note => ({
          program: note.getAttribute('data-program'),
          instrument: note.getAttribute('data-instrument'),
          channel: note.getAttribute('data-channel'),
          isDrum: note.getAttribute('data-is-drum')
        })));
      }
    }
  }, 1000);
};

// Function to generate default color CSS when MIDI analysis fails
export const generateDefaultColorCSS = (visualizerId) => {
  let cssRules = '';
  
  // Apply first few colors to common instrument numbers
  for (let i = 0; i < Math.min(10, SEABORN_TAB20_COLORS.length); i++) {
    const baseColor = hexToRgba(SEABORN_TAB20_COLORS[i], activeOpacity);
    const restColor = hexToRgba(SEABORN_TAB20_COLORS[i], restOpacity);
    
    cssRules += `
      #${visualizerId} svg rect.note[data-instrument="${i}"] {
        fill: ${restColor} !important;
      }
      
      #${visualizerId} svg rect.note[data-instrument="${i}"].active {
        fill: ${baseColor} !important;
        opacity: ${activeOpacity} !important;
        filter: brightness(1.2) saturate(1.2) !important;
      }
    `;
  }
  
  // Default drum styling - target multiple possible selectors
  cssRules += `
    #${visualizerId} svg rect.note[data-is-drum="true"],
    #${visualizerId} svg rect.note[data-channel="9"],
    #${visualizerId} svg rect.note[data-program="128"],
    #${visualizerId} svg rect.note[data-instrument="128"] {
      fill: ${hexToRgba('#7f7f7f', restOpacity)} !important;
      opacity: 0.8 !important;
    }
    
    #${visualizerId} svg rect.note[data-is-drum="true"].active,
    #${visualizerId} svg rect.note[data-channel="9"].active,
    #${visualizerId} svg rect.note[data-program="128"].active,
    #${visualizerId} svg rect.note[data-instrument="128"].active {
      fill: #7f7f7f !important;
      opacity: ${activeOpacity} !important;
      filter: brightness(1.2) saturate(1.2) !important;
    }
  `;
  
  // Create or update style element
  let styleElement = document.getElementById(`midi-colors-${visualizerId}`);
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = `midi-colors-${visualizerId}`;
    document.head.appendChild(styleElement);
  }
  
  styleElement.textContent = cssRules;
  console.log('Applied default color scheme');
};

// Function to configure basic visualizer settings
export const getVisualizerConfig = () => {
  return {
    noteHeight: 5,
    pixelsPerTimeStep: 100,
  };
};

// Function to apply scaling and positioning to visualizer
export const applyVisualizerScaling = (visualizer, maxHeight = 320) => {
  // Get the actual content height after rendering using getBoundingClientRect for accuracy
  const canvas = visualizer.querySelector('canvas');
  const svg = visualizer.querySelector('svg');
  const contentElement = canvas || svg;
  
  if (contentElement) {
    // Use getBoundingClientRect to get the true rendered size
    const rect = contentElement.getBoundingClientRect();
    const naturalHeight = rect.height;
    
    if (naturalHeight > maxHeight) {
      // Scale down only the height to fit, keep width at 100%
      const scaleFactor = maxHeight / naturalHeight;
      visualizer.style.transform = `scaleY(${scaleFactor}) translateY(24px)`;
      visualizer.style.transformOrigin = 'center top';
      visualizer.style.width = '100%';
      
      // Center vertically, align left horizontally
      visualizer.style.display = 'flex';
      visualizer.style.alignItems = 'center';
      visualizer.style.justifyContent = 'flex-start';
    } else {
      // Use natural height if it fits, keep full width, align left
      visualizer.style.transform = 'none';
      visualizer.style.width = '100%';
      visualizer.style.display = 'flex';
      visualizer.style.alignItems = 'center';
      visualizer.style.justifyContent = 'flex-start';
    }
  }
};

// Function to create instrument visibility controls
export const createInstrumentControls = (visualizerId, instrumentOrder, hasDrums = false) => {
  const controlsContainer = document.createElement('div');
  controlsContainer.className = 'instrument-controls';
  controlsContainer.id = `controls-${visualizerId}`;
  
  // State to track which instruments are visible
  const visibilityState = {};
  instrumentOrder.forEach(instrument => {
    visibilityState[instrument] = true; // All visible by default
  });
  
  // Add drums to visibility state if present
  if (hasDrums) {
    visibilityState['drums'] = true;
  }
  
  // Helper function to update visibility CSS
  const updateVisibility = () => {
    let visibilityRules = '';
    
    Object.entries(visibilityState).forEach(([instrument, isVisible]) => {
      if (instrument === 'drums') {
        // Special handling for drums - use multiple selectors
        visibilityRules += `
          #${visualizerId} svg rect.note[data-is-drum="true"],
          #${visualizerId} svg rect.note[data-channel="9"],
          #${visualizerId} svg rect.note[data-program="128"],
          #${visualizerId} svg rect.note[data-instrument="128"] {
            display: ${isVisible ? 'block' : 'none'} !important;
          }
        `;
      } else {
        visibilityRules += `
          #${visualizerId} svg rect.note[data-program="${instrument}"],
          #${visualizerId} svg rect.note[data-instrument="${instrument}"] {
            display: ${isVisible ? 'block' : 'none'} !important;
          }
        `;
      }
    });
    
    // Update or create visibility style element
    let visibilityStyleElement = document.getElementById(`midi-visibility-${visualizerId}`);
    if (!visibilityStyleElement) {
      visibilityStyleElement = document.createElement('style');
      visibilityStyleElement.id = `midi-visibility-${visualizerId}`;
      document.head.appendChild(visibilityStyleElement);
    }
    visibilityStyleElement.textContent = visibilityRules;
  };
  


  // Helper function to update the "All" button state
  const updateAllButtonState = () => {
    const allVisible = Object.values(visibilityState).every(visible => visible);
    if (allVisible) {
      // All are shown - button should be highlighted (active state)
      allButton.classList.remove('subdued');
    } else {
      // Some are hidden - button should be subdued (desaturated)
      allButton.classList.add('subdued');
    }
  };

  // Create "Show Me:" label
  const showMeLabel = document.createElement('span');
  showMeLabel.textContent = 'Show Me:';
  showMeLabel.className = 'instrument-controls-label';

  // Create main instrument buttons container
  const mainButtonsContainer = document.createElement('div');
  mainButtonsContainer.className = 'main-buttons';

  // Create "All" button
  const allButton = document.createElement('button');
  allButton.textContent = 'All';
  allButton.className = 'instrument-btn instrument-btn-all active';
  allButton.onclick = () => {
    // Show all instruments
    Object.keys(visibilityState).forEach(instrument => {
      visibilityState[instrument] = true;
    });
    updateVisibility();
    
    // Update button states and restore colors
    controlsContainer.querySelectorAll('.instrument-btn:not(.instrument-btn-all):not(.instrument-btn-empty)').forEach(btn => {
      btn.classList.add('active');
      // Restore the original background color
      const instrument = btn.getAttribute('data-instrument');
      if (instrument === 'drums') {
        btn.style.backgroundColor = '#000000';
      } else {
        const instrumentIndex = instrumentOrder.indexOf(parseInt(instrument));
        if (instrumentIndex !== -1 && instrumentIndex < COLOR_SEQUENCE.length) {
          const colorIndex = COLOR_SEQUENCE[instrumentIndex];
          const restColor = SEABORN_TAB20_COLORS[colorIndex];
          btn.style.backgroundColor = restColor;
        }
      }
    });
    
    updateAllButtonState();
  };
  mainButtonsContainer.appendChild(allButton);

  // Create "Empty" button (subdued, secondary style)
  const emptyButton = document.createElement('button');
  emptyButton.textContent = 'Empty';
  emptyButton.className = 'instrument-btn instrument-btn-empty';
  emptyButton.onclick = () => {
    // Hide all instruments
    Object.keys(visibilityState).forEach(instrument => {
      visibilityState[instrument] = false;
    });
    updateVisibility();
    
    // Update button states
    controlsContainer.querySelectorAll('.instrument-btn:not(.instrument-btn-all):not(.instrument-btn-empty)').forEach(btn => {
      btn.classList.remove('active');
      btn.style.backgroundColor = 'transparent';
    });
    
    updateAllButtonState();
  };
  
  controlsContainer.appendChild(showMeLabel);
  controlsContainer.appendChild(mainButtonsContainer);
  
  // Create individual instrument buttons
  instrumentOrder.forEach((instrument, index) => {
    if (index < COLOR_SEQUENCE.length) {
      const colorIndex = COLOR_SEQUENCE[index];
      const activeColor = SEABORN_TAB20_COLORS[colorIndex];
      const restColor = SEABORN_TAB20_COLORS[colorIndex];
      
      const button = document.createElement('button');
      const instrumentInfo = getInstrumentInfo(instrument);
      button.textContent = instrumentInfo.abbr;
      button.className = 'instrument-btn active';
      
      // Add floating tooltip functionality with proper cleanup
      let tooltip = null;
      let showTimeout = null;
      let hideTimeout = null;
      
      button.addEventListener('mouseenter', (e) => {
        // Clear any pending hide operation
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          hideTimeout = null;
        }
        
        // Remove existing tooltip if any
        if (tooltip && tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
        
        // Create new tooltip
        tooltip = document.createElement('div');
        tooltip.className = 'floating-tooltip';
        tooltip.textContent = instrumentInfo.name;
        document.body.appendChild(tooltip);
        
        const buttonRect = e.target.getBoundingClientRect();
        tooltip.style.left = (buttonRect.left + buttonRect.width / 2) + 'px';
        tooltip.style.top = (buttonRect.top - 8) + 'px';
        tooltip.style.transform = 'translateX(-50%) translateY(-100%)';
        
        // Show tooltip with delay
        showTimeout = setTimeout(() => {
          if (tooltip && tooltip.classList) {
            tooltip.classList.add('visible');
          }
          showTimeout = null;
        }, 10);
      });
      
      button.addEventListener('mouseleave', () => {
        // Clear any pending show operation
        if (showTimeout) {
          clearTimeout(showTimeout);
          showTimeout = null;
        }
        
        if (tooltip) {
          // Hide tooltip immediately
          if (tooltip.classList) {
            tooltip.classList.remove('visible');
          }
          
          // Remove tooltip after transition
          hideTimeout = setTimeout(() => {
            if (tooltip && tooltip.parentNode) {
              tooltip.parentNode.removeChild(tooltip);
            }
            tooltip = null;
            hideTimeout = null;
          }, 200);
        }
      });
      button.style.backgroundColor = restColor;
      button.style.borderColor = activeColor;
      button.style.setProperty('--active-color', activeColor);
      button.style.minWidth = '24px';
      button.style.height = '20px';
      button.style.padding = '2px 6px';
      button.setAttribute('data-instrument', instrument);
      
      button.onclick = () => {
        // Toggle visibility
        visibilityState[instrument] = !visibilityState[instrument];
        updateVisibility();
        
        // Update button appearance
        if (visibilityState[instrument]) {
          button.classList.add('active');
          button.style.backgroundColor = restColor;
        } else {
          button.classList.remove('active');
          button.style.backgroundColor = 'transparent';
        }
                
        updateAllButtonState();
        };
        
        mainButtonsContainer.appendChild(button);
      }
    });
    
    // Create drums button if drums are present
    if (hasDrums) {
      const drumsButton = document.createElement('button');
      drumsButton.textContent = '🥁';
      drumsButton.className = 'instrument-btn active';
      
      // Add floating tooltip functionality with proper cleanup
      let drumsTooltip = null;
      let drumsShowTimeout = null;
      let drumsHideTimeout = null;
      
      drumsButton.addEventListener('mouseenter', (e) => {
        // Clear any pending hide operation
        if (drumsHideTimeout) {
          clearTimeout(drumsHideTimeout);
          drumsHideTimeout = null;
        }
        
        // Remove existing tooltip if any
        if (drumsTooltip && drumsTooltip.parentNode) {
          drumsTooltip.parentNode.removeChild(drumsTooltip);
        }
        
        // Create new tooltip
        drumsTooltip = document.createElement('div');
        drumsTooltip.className = 'floating-tooltip';
        drumsTooltip.textContent = 'Drums';
        document.body.appendChild(drumsTooltip);
        
        const buttonRect = e.target.getBoundingClientRect();
        drumsTooltip.style.left = (buttonRect.left + buttonRect.width / 2) + 'px';
        drumsTooltip.style.top = (buttonRect.top - 8) + 'px';
        drumsTooltip.style.transform = 'translateX(-50%) translateY(-100%)';
        
        // Show tooltip with delay
        drumsShowTimeout = setTimeout(() => {
          if (drumsTooltip && drumsTooltip.classList) {
            drumsTooltip.classList.add('visible');
          }
          drumsShowTimeout = null;
        }, 10);
      });
      
      drumsButton.addEventListener('mouseleave', () => {
        // Clear any pending show operation
        if (drumsShowTimeout) {
          clearTimeout(drumsShowTimeout);
          drumsShowTimeout = null;
        }
        
        if (drumsTooltip) {
          // Hide tooltip immediately
          if (drumsTooltip.classList) {
            drumsTooltip.classList.remove('visible');
          }
          
          // Remove tooltip after transition
          drumsHideTimeout = setTimeout(() => {
            if (drumsTooltip && drumsTooltip.parentNode) {
              drumsTooltip.parentNode.removeChild(drumsTooltip);
            }
            drumsTooltip = null;
            drumsHideTimeout = null;
          }, 200);
        }
      });
      const drumsRestColor = '#000000';
      const drumsActiveColor = '#000000';
      drumsButton.style.backgroundColor = drumsRestColor;
      drumsButton.style.borderColor = drumsActiveColor;
      drumsButton.style.setProperty('--active-color', drumsActiveColor);
      drumsButton.style.fontSize = '0.9rem';
      drumsButton.style.minWidth = '24px';
      drumsButton.style.height = '20px';
      drumsButton.style.padding = '2px 6px';
      drumsButton.setAttribute('data-instrument', 'drums');
      
      drumsButton.onclick = () => {
        // Toggle visibility
        visibilityState['drums'] = !visibilityState['drums'];
        updateVisibility();
        
        // Update button appearance
        if (visibilityState['drums']) {
          drumsButton.classList.add('active');
          drumsButton.style.backgroundColor = drumsRestColor;
        } else {
          drumsButton.classList.remove('active');
          drumsButton.style.backgroundColor = 'transparent';
        }
                
        updateAllButtonState();
      };
        
      mainButtonsContainer.appendChild(drumsButton);
    }
    
      // Add empty button to the right side
  controlsContainer.appendChild(emptyButton);
  
  // Set initial button states
  updateAllButtonState();
  
  return controlsContainer;
};

// Function to setup colors and controls for a visualizer
export const setupVisualizerColors = (visualizerId, dataUrl) => {
  analyzeMidiInstruments(dataUrl)
    .then(result => {
      const { instruments, hasDrums } = result;
      generateInstrumentColorCSS(visualizerId, instruments);
      
      // Add instrument controls
      const visualizerContainer = document.getElementById(visualizerId).parentElement;
      if (visualizerContainer) {
        // Remove existing controls if any
        const existingControls = visualizerContainer.querySelector('.instrument-controls');
        if (existingControls) {
          existingControls.remove();
        }
        
        // Add new controls
        const controls = createInstrumentControls(visualizerId, instruments, hasDrums);
        visualizerContainer.appendChild(controls);
      }
    })
    .catch(error => {
      console.warn('Failed to apply instrument colors, using defaults:', error);
      generateDefaultColorCSS(visualizerId);
    });
}; 